import { BaseApi, type ItemResponse } from '@bv/api';

import { useQueuesDataStore } from '@/stores/reports/home.store';

export interface Summary {
  total_calls: number;
  analyzed_calls: { total: number; inbound: number; outbound: number };
  average_sentiment: {
    score: string;
    score_last_day: string;
    trend_arrow: 'up' | 'down';
  };
  total_tagged_calls: number;
  manual_tagged_calls: number;
  auto_tagged_calls: number;
}

export interface TagDistributionItem {
  tag: {
    name: string;
    color: string;
  };
  num_calls: number;
}

interface SentimentItemValue {
  num_calls: number;
  percentage: string | number;
}

interface TagDistributionResponse {
  data: TagDistributionItem[];
  meta: {
    total_tags: number;
    total_calls: number;
    total_used_calls: number;
  };
}

export type OverallSentiment = Record<'positive' | 'neutral' | 'negative', SentimentItemValue>;

class AiDashboardApi extends BaseApi {
  public constructor() {
    super('reports/call-analysis/dashboard');
  }

  private async getTimezone() {
    await useQueuesDataStore().fetchQueuesDataIfNotFetched();
    return await useQueuesDataStore().serverTimezone;
  }

  public async summary(): Promise<Summary> {
    const timezone = await this.getTimezone();

    return this.request<unknown, ItemResponse<Summary>>({
      method: 'get',
      url: 'summary',
      params: { timezone },
    }).then((response) => response.data);
  }

  public async overallSentiment(): Promise<OverallSentiment> {
    const timezone = await this.getTimezone();

    return this.request<unknown, ItemResponse<OverallSentiment>>({
      method: 'get',
      url: 'overall-sentiment',
      params: { timezone },
    }).then((response) => response.data);
  }

  public async tagDistribution(top?: number): Promise<TagDistributionResponse> {
    const timezone = await this.getTimezone();

    return this.request<unknown, TagDistributionResponse>({
      method: 'get',
      url: 'tag-distribution',
      params: { timezone, top },
    });
  }
}

export const aiDashboardApi = new AiDashboardApi();
